'use client';

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { verifyEmailKey } from '@/shared/services/authServices';
import { useToast } from '@/shared/contexts';

type State = 'loading' | 'success' | 'error';

export default function VerifyPage() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { setOpenVerify } = useToast();

  const [state, setState] = useState<State>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!key) {
      setState('error');
      setMessage('Link invalido.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyEmailKey(key);
        if (cancelled) return;
        if (res?.Status === 'Sucesso' || res?.status === 'Sucesso') {
          setState('success');
          setMessage(res.Message || res.message || 'Conta verificada com sucesso.');
        } else {
          setState('error');
          setMessage(res?.Message || res?.message || 'Link expirado, solicite um novo.');
        }
      } catch (e: any) {
        if (cancelled) return;
        setState('error');
        setMessage(e?.Message || e?.message || 'Link expirado, solicite um novo.');
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  return (
    <div className="relative flex h-screen w-full flex-col items-center justify-center overflow-hidden bg-[#05070C] text-white">
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.62) 50%, rgba(0,0,0,0.88) 100%), url('/assets/bg3.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        aria-hidden
      />

      <div className="relative z-10 mx-4 w-full max-w-md rounded-2xl border border-white/10 bg-black/60 p-8 text-center backdrop-blur-xl">
        {state === 'loading' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#00E1D7]" />
            <h1 className="mt-4 text-2xl font-extrabold">Verificando...</h1>
            <p className="mt-2 text-sm text-white/65">Estamos confirmando sua conta.</p>
          </>
        )}

        {state === 'success' && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#00E1D7]" />
            <h1 className="mt-4 text-2xl font-extrabold">Conta verificada!</h1>
            <p className="mt-2 text-sm text-white/65">{message}</p>
            <div className="mt-6 grid gap-3">
              <Button
                className="h-12 w-full rounded-xl bg-[#00E1D7] text-base font-extrabold uppercase tracking-wider text-black transition hover:bg-[#00E1D7]/90"
                onClick={() => { navigate('/'); setOpenVerify(true); }}
              >
                Fazer login
              </Button>
              <button
                type="button"
                className="text-xs text-white/55 hover:text-white/80"
                onClick={() => navigate('/')}
              >
                Voltar pra home
              </button>
            </div>
          </>
        )}

        {state === 'error' && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-[#FA0000]" />
            <h1 className="mt-4 text-2xl font-extrabold">Não foi possível verificar</h1>
            <p className="mt-2 text-sm text-white/65">{message}</p>
            <div className="mt-6 grid gap-3">
              <Button
                className="h-12 w-full rounded-xl bg-[#00E1D7] text-base font-extrabold uppercase tracking-wider text-black transition hover:bg-[#00E1D7]/90"
                onClick={() => navigate('/cadastro')}
              >
                Tentar cadastrar novamente
              </Button>
              <button
                type="button"
                className="text-xs text-white/55 hover:text-white/80"
                onClick={() => navigate('/')}
              >
                Voltar pra home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
